import React, { useEffect, useState } from 'react'
import { Button, Col, Form, InputGroup, Modal, Row, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Chip, Paper, styled, Tooltip } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { AuctionBase } from '../../app/models/Auction'
import { useStore } from '../../app/stores/store'
import ErrorMessage from '../components/ErrorMessage'
import AuctionCreateDates from '../components/auction/AuctionCreateDates'
import { KeyboardCodes } from '../../app/enums/event'
import { WizardStep } from '../../app/enums/wizard'
import { AuctionCreationDatePickersInterface } from '../../app/models/date'
import { AuctionPublic } from '../../app/models/modes'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'

const AuctionCreate = ({ handleClose }: any) => {
  const { auctionListStore, postStore, userStore, auctionStore } = useStore()
  const initialState: AuctionBase = {
    minIncreasePrice: 0,
    deposit: 0,
    minPrice: 0,
    totalRounds: 0,
    nextRoundUsers: 0,
    numberOfMembers: 0,
    allowBelowMin: false,
    startingDate: new Date(),
    deadline: new Date(),
    public: 0,
    attachment: false,
    invitedUsers: [],
    authorizedMods: [],
    postId: '',
    startImmediately: false,
    winner: '',
  }
  const [auction, setAuction] = useState(initialState)
  const [wizardStep, setWizardStep] = useState<WizardStep>(WizardStep.Step_1)
  const [emailInput, setEmailInput] = useState<any>('')
  const [numberOfDaysPerRound, setNumberForDaysPerRound] = useState<number>(0)
  const [error, setError] = useState('')
  const [loadingBtn, setLoadingBtn] = useState(false)
  const { t } = useTranslation()
  const MAX_INT_VALUE = 100000000
  const [deadlineDateForAuction, setDeadlineDateForAuction] = useState<Date>()
  const deadlineToShow = new Date()

  useEffect(() => {
    postStore.loadMyPosts()
    return () => {
      postStore.myPostRegistry.clear()
    }
  }, [postStore])

  useEffect(() => {
    userStore.getModerators()
  }, [userStore])

  useEffect(() => {
    if (!deadlineDateForAuction) {
      setDeadlineDateForAuction(auction.deadline)
    }
  }, [])

  useEffect(() => {
    if (auction.totalRounds) {
      recheckDeadlineDate()
    }
  }, [auction.totalRounds, auction.startImmediately, auction.startingDate, numberOfDaysPerRound])

  const handleSubmit = (e: any) => {
    e.preventDefault()
    setLoadingBtn(true)

    if (auction.public === AuctionPublic.Private && auction.invitedUsers?.length === 0) {
      setError(t('err_invitedUsers')!)
      setLoadingBtn(false)
    } else if (auction.attachment && auction.authorizedMods?.length === 0) {
      setError(t('err_authMods')!)
      setLoadingBtn(false)
    } else if (!auction.startImmediately && !auction.startingDate) {
      setError(t('err_startingDate')!)
      setLoadingBtn(false)
    } else if (!auction.deadline) {
      setError(t('err_deadline')!)
      setLoadingBtn(false)
    } else if (
      auction.public === AuctionPublic.Private &&
      auction.invitedUsers?.length &&
      auction.numberOfMembers < auction.invitedUsers.length
    ) {
      setError('auction.invited_more_than_config_set')
      setLoadingBtn(false)
    } else {
      setError('')
      auctionStore
        .createAuction(auction)
        .then(() => {
          auctionListStore.loadAuctions()
        })
        .finally(() => setLoadingBtn(false))
    }
  }

  const handleNext = () => {
    if (!auction.postId) {
      setError(t('err_post') || '')
      return
    }
    const isValid = checkNumericValues()
    if (!isValid) return

    if (auction.nextRoundUsers <= 1) {
      setError(t('err_higher_nextRoundUsers') || '')
    } else {
      setError('')
      setWizardStep(wizardStep + 1)
    }
  }

  const handlePrev = () => {
    setWizardStep(wizardStep - 1)
  }

  const handleCheckChange = (e: any) => {
    const { name } = e.target
    setAuction({ ...auction, [name]: !auction[name as keyof AuctionBase] })
  }

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    const numericValue = parseInt(value)

    if (!isNaN(numericValue) && numericValue > 0) {
      let maxValue = MAX_INT_VALUE

      if (name === 'totalRounds' || name === 'nextRoundUsers') {
        maxValue = 20
      } else if (name === 'numberOfMembers') {
        maxValue = 1000
      } else if (name === 'minIncreasePrice' || name === 'deposit') {
        maxValue = 1000000
      }

      const sanitizedValue = Math.min(numericValue, maxValue)

      setAuction({ ...auction, [name]: sanitizedValue })
    } else {
      setAuction({ ...auction, [name]: 1 })
    }
  }

  function handleInputChange(e: any) {
    const { name, value } = e.target
    setAuction({ ...auction, [name]: value })
  }

  const checkNumericValues = () => {
    const relevantInputCols = wizardStep === WizardStep.Step_1 ? inputCols.slice(0, 6) : inputCols

    for (const { name } of relevantInputCols) {
      const value = auction[name as keyof AuctionBase]

      if (name === 'minPrice' && auction.allowBelowMin) {
        continue
      }

      if (typeof value === 'number' && (value === 0 || Number.isNaN(value))) {
        const errName = `err_${name}`
        setError(t(errName) || '')
        return false
      }
    }
    return true
  }

  const handleAddUserEmail = () => {
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(emailInput)) {
      setError(t('invalid_email')!)
    } else if (auction.invitedUsers!.includes(emailInput)) {
      setError(t('duplicate_email_err')!)
    } else {
      const auctionClone = { ...auction }
      auctionClone.invitedUsers!.push(emailInput)
      setEmailInput('')
      setError('')
      setAuction(auctionClone)
    }
  }

  const handleUserInviteDelete = (index: number) => () => {
    const auctionClone = { ...auction }
    auctionClone.invitedUsers = (auctionClone.invitedUsers as string[]).filter(
      (email, i) => i !== index,
    )
    setAuction(auctionClone)
  }

  // const handleChangeModerator = (event: any) => {
  //   const {
  //     target: { value },
  //   } = event
  //   setAuction({
  //     ...auction,
  //     authorizedMods: typeof value === 'string' ? value.split(',') : value,
  //   })
  // }

  const handleAddEmailOnEnter = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === KeyboardCodes.Enter) {
      handleAddUserEmail()
    }
  }

  const recheckDeadlineDate = () => {
    const auctionClone = { ...auction }
    const finalDatePerFirstRound = auctionClone.startingDate.getDate() + numberOfDaysPerRound
    const finalDatePerWholeAuction =
      auctionClone.startingDate.getDate() + numberOfDaysPerRound * auctionClone.totalRounds

    deadlineToShow.setDate(finalDatePerWholeAuction)
    setDeadlineDateForAuction(deadlineToShow)
    auctionClone.deadline.setDate(finalDatePerFirstRound)
    setAuction(auctionClone)
  }

  const handleDaysPerRound = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const target = e.target as HTMLInputElement
    const numberOfDaysPerRoundConverted = Number(target.value)
    setNumberForDaysPerRound(numberOfDaysPerRoundConverted)
  }

  const inputCols = [
    {
      md: 4,
      label: t('increase_price'),
      placeholder: '100',
      name: 'minIncreasePrice',
    },
    {
      md: 4,
      label: t('deposit'),
      placeholder: '1 000',
      name: 'deposit',
    },
    {
      md: 4,
      label: t('min_price'),
      placeholder: '9 000',
      disabled: auction.allowBelowMin,
      name: 'minPrice',
    },
    {
      md: 4,
      label: t('total_round'),
      placeholder: '3',
      name: 'totalRounds',
    },
    {
      md: 4,
      label: t('nextRoundUsers'),
      placeholder: '6',
      name: 'nextRoundUsers',
    },
    {
      md: 4,
      label: t('numberOfMembers'),
      placeholder: '10',
      name: 'numberOfMembers',
    },
  ]

  const privacySettings = [
    { value: 0, label: t('public_offer') },
    { value: 1, label: t('private_offer') },
  ]

  const buttons = [
    {
      title: t('cancel'),
      onClick: handleClose,
      className: 'payment-button-cancel',
      wizardStep: WizardStep.Step_1,
    },
    {
      title: t('next'),
      onClick: handleNext,
      className: 'payment-button-tryagain',
      wizardStep: WizardStep.Step_1,
    },
    {
      title: t('previous'),
      onClick: handlePrev,
      className: 'payment-button-cancel',
      wizardStep: WizardStep.Step_2,
    },
    {
      title: loadingBtn ? <Spinner animation='border' variant='light' /> : t('create'),
      className: 'payment-button-tryagain',
      wizardStep: WizardStep.Step_2,
      type: 'submit',
    },
  ]

  const formChecks = [
    // TODO: enable afterwards
    // {
    //   title: t('solvency'),
    //   label: t('enable_solvency'),
    //   name: 'attachment',
    //   checked: auction.attachment,
    //   component: (
    //     <ModeratorsSelect
    //       auction={auction}
    //       userStore={userStore}
    //       handleChange={handleChangeModerator}
    //     />
    //   ),
    // },
    {
      title: t('options'),
      label: t('startImmediately'),
      name: 'startImmediately',
      checked: auction.startImmediately,
    },
  ]

  const dates: AuctionCreationDatePickersInterface[] = [
    {
      lg: 6,
      label: t('starting_date'),
      value: auction.startingDate,
      onChange: 'startingDate',
      style: {
        paddingLeft: 0,
        marginTop: '20px',
      },
      disabled: auction.startImmediately,
    },
    {
      lg: 6,
      label: t('deadline'),
      value: auction.deadline,
      disabled: auction.totalRounds > 1,
      onChange: 'deadline',
      style: {
        paddingRight: 0,
        marginTop: '20px',
      },
      tooltip: t('auction_deadline_tooltip') || '',
    },
  ]

  const ListItem = styled('li')(({ theme }) => ({
    margin: theme.spacing(0.5),
  }))

  return (
    <div className='newFont prevent-select'>
      <Modal.Header className='bg-white'>
        <p className='post-card-title'>
          {t('create_new_offer')} / {t('auction')}
        </p>
        <Button variant='inherit' className='close me-2' onClick={handleClose}>
          <i className='fa-regular fa-x icon-size' />
        </Button>
      </Modal.Header>
      <Modal.Body className='post-card-padding pt-5 card-font-size'>
        <Form onSubmit={handleSubmit}>
          {wizardStep === WizardStep.Step_1 && (
            <>
              <Form.Group className='mb-3'>
                <Form.Select
                  className='card-input-style form-select-auction'
                  placeholder='Title'
                  onChange={handleInputChange}
                  name='postId'
                  value={auction.postId}
                >
                  <option value=''>{t('select_post')}</option>
                  {postStore.usersPosts.map((post) => (
                    <option key={post.id} value={post.id}>
                      {post.title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Row className='text-center center-input pb-4 auction-create-modal'>
                {inputCols.map(({ md, label, placeholder, disabled, name }, index) => (
                  <Col key={index} lg={md}>
                    <Form.Label className='login-p'>{label}</Form.Label>
                    {name === 'minPrice' ? (
                      <InputGroup>
                        <Form.Control
                          className='card-input-style'
                          placeholder={placeholder}
                          type='number'
                          disabled={disabled}
                          name={name}
                          required
                          onChange={handleNumberChange}
                          value={auction[name] === 0 || disabled ? '' : auction[name]}
                        />
                      </InputGroup>
                    ) : (
                      <Form.Control
                        className='card-input-style'
                        placeholder={placeholder}
                        type='number'
                        disabled={disabled}
                        name={name}
                        required
                        onChange={handleNumberChange}
                        value={auction[name] === 0 || disabled ? '' : auction[name]}
                      />
                    )}
                  </Col>
                ))}
              </Row>
              <hr className='auction-create-hr' />
              <div className='pt-3 card-font-size'>
                <p className='text-black fw-bold'>{t('config_settings')}</p>
                <Form.Group className='d-flex gap-content text-black pt-2'>
                  <label className='d-flex gap-2'>
                    <Form.Check
                      type='checkbox'
                      className='text-black mb-0'
                      name='allowBelowMin'
                      checked={auction.allowBelowMin}
                      onChange={handleCheckChange}
                    />
                    {t('allowBelowMin')}
                  </label>
                </Form.Group>
              </div>
            </>
          )}
          {wizardStep === WizardStep.Step_2 && (
            <>
              <div className='card-font-size'>
                <p className='text-black fw-bold'>{t('privacy_settings')}</p>
                <Form.Group className='d-flex align-items-center gap-content text-black pt-2 center-content'>
                  {privacySettings.map(({ value, label }, index) => (
                    <label className='d-flex gap-2 text-center' key={index}>
                      <Form.Check
                        type='radio'
                        className='text-black mb-0'
                        name='public'
                        value={value}
                        checked={auction.public === value}
                        onChange={() => setAuction({ ...auction, public: value })}
                      />
                      {label}
                    </label>
                  ))}
                </Form.Group>
              </div>
              <br />
              {auction.public === 1 && (
                <>
                  {auction.invitedUsers!.length > 0 && (
                    <Paper className='private-auction-participants' component='ul'>
                      {auction.invitedUsers?.map((data, index) => (
                        <ListItem sx={{ color: 'white' }} key={index}>
                          <Chip
                            label={data}
                            sx={{ color: 'black' }}
                            onDelete={handleUserInviteDelete(index)}
                          />
                        </ListItem>
                      ))}
                    </Paper>
                  )}
                  <InputGroup className='mb-3 mt-4 card-input-style-wth-btn'>
                    <Form.Control
                      value={emailInput}
                      onKeyUp={handleAddEmailOnEnter}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder={t('participant_email')!}
                      type='email'
                    />
                    <button type='button' onClick={handleAddUserEmail}>
                      {t('add')}
                    </button>
                  </InputGroup>
                </>
              )}
              <br />
              {formChecks.map((form, index) => (
                <React.Fragment key={index}>
                  <div className='card-font-size'>
                    <p className='text-black fw-bold'>{form.title}</p>
                    <Form.Group className='d-flex gap-content text-black pt-2'>
                      <label className='d-flex gap-2'>
                        <Form.Check
                          type='checkbox'
                          className='text-black mb-0'
                          name={form.name}
                          checked={form.checked}
                          onChange={handleCheckChange}
                        />
                        {form.label}
                      </label>
                    </Form.Group>
                    {/* {auction.attachment && <div className='mt-4'>{form.component}</div>} */}
                  </div>
                  {index === 0 && <br />}
                </React.Fragment>
              ))}

              <AuctionCreateDates
                dates={dates}
                setAuction={setAuction}
                auction={auction}
                deadlineToShow={deadlineDateForAuction}
              />

              {auction.totalRounds > 1 && (
                <Row className='text-center center-input pb-4 auction-create-modal'>
                  <Col sm={12} lg={6}>
                    <Form.Label className='login-p'>
                      {t('total_days_per_round.label')}
                      <Tooltip className='mx-2' title={t('total_days_per_round.tooltip')}>
                        <HelpOutlineIcon />
                      </Tooltip>
                    </Form.Label>

                    <Form.Control
                      className='card-input-style'
                      placeholder={t('total_days_per_round.placeholder') || ''}
                      type='number'
                      required
                      onChange={handleDaysPerRound}
                    />
                  </Col>
                </Row>
              )}
            </>
          )}
          {error && <ErrorMessage text={error} setError={() => setError('')} />}

          <div className='auction-create-buttons'>
            {buttons.map(
              (button, index) =>
                wizardStep === button.wizardStep && (
                  <button
                    key={index}
                    onClick={button.onClick}
                    className={`text-white ${button.className}`}
                    type={button.type === 'submit' ? 'submit' : 'button'}
                    disabled={loadingBtn}
                  >
                    {button.title}
                  </button>
                ),
            )}
          </div>
        </Form>
      </Modal.Body>
    </div>
  )
}

export default observer(AuctionCreate)
